import { Popover } from '@material-ui/core';
import CSS from 'csstype';
import { debounce } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FixedSizeList } from 'react-window';

import { Option } from '~/src/dash/components/dropdowns/SimpleSingleSelect/Option';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  ISimpleOptionSelect,
  Row,
  Rows,
  spacingV2,
  Text,
  TextInput,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface ISimpleSingleSelectProps {
  isDisabled?: boolean;
  isSearchEnabled?: boolean;
  isSortEnabled?: boolean;
  inputWidth?: string;
  onChange: (selectedValue: string) => void; // returns selectedIDs to the parent
  options: ISimpleOptionSelect[];
  placeholder?: string;
  placeholderTKey?: TKey;
  popoverWidth?: string;
  sortFunc?: (a: ISimpleOptionSelect, b: ISimpleOptionSelect) => number; // custom sort function
  style?: CSS.Properties;
}

export const SimpleSingleSelect = ({
  isDisabled = false,
  isSearchEnabled = false,
  isSortEnabled = false,
  inputWidth = 'inherit',
  onChange,
  options,
  placeholder = '',
  placeholderTKey = '',
  popoverWidth = 'inherit',
  sortFunc,
  style,
}: ISimpleSingleSelectProps) => {
  const { t } = usePublicaTranslation();

  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [displayedOptions, setDisplayedOptions] = useState<ISimpleOptionSelect[]>([]);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<ISimpleOptionSelect | null | undefined>(
    null,
  );

  // max number of options that we display at once
  const itemSize = 30;

  // helper function to sort options
  const sortOptions = (list: ISimpleOptionSelect[]) => {
    if (!isSortEnabled) return list;
    if (sortFunc) return list.sort((a, b) => sortFunc(a, b));

    // by default, we will sort based on selected -> value
    return list.sort((a, b) => {
      if (a.isSelected === b.isSelected) {
        return a.label.localeCompare(b.label);
      } else if (a.isSelected !== b.isSelected) {
        return a.isSelected > b.isSelected ? -1 : 1;
      } else {
        return 0;
      }
    });
  };

  // when user types into the search box
  const onSearchUpdate = () => {
    // reset to show all options if search is cleared
    if (!search) {
      setDisplayedOptions(options);
    }

    // find all matched options
    const matchedOptions = options.filter(
      (item) => String(item.value).includes(search) || item.label.toLowerCase().includes(search),
    );
    setDisplayedOptions(matchedOptions);
  };

  const debouncedSearchUpdate = useMemo(() => {
    return debounce(onSearchUpdate, 400);
  }, [onSearchUpdate]);

  // open dropdown
  const onOpenDropdown = (e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget);
  };

  // close dropdown
  const onCloseDropdown = () => {
    setSearch('');
    setAnchorEl(null);
  };

  // handler when user clicks on an option
  const handleChange = (newOption: ISimpleOptionSelect) => {
    // find the option that was clicked on, return if nothing is found
    const selectedOption = options.find((option) => option.value === newOption.value);
    if (!selectedOption) return;

    // otherwise, update the state, execute onChange, and close the dropdown
    const updatedOptions = options.map((opt) => ({
      ...opt,
      selected: opt.value === newOption.value,
    }));
    unstable_batchedUpdates(() => {
      setDisplayedOptions(sortOptions(updatedOptions));
      setSelectedOption(selectedOption);
      onChange(selectedOption.value);
      onCloseDropdown();
    });
  };

  // handler whenever prop options changes
  useEffect(() => {
    if (options) {
      setDisplayedOptions(sortOptions(options));
      setSelectedOption(options.find((opt) => opt.isSelected));
    }
  }, [JSON.stringify(options)]);

  // handler whenever the search term changes
  useEffect(() => {
    debouncedSearchUpdate();
  }, [search]);

  return (
    <>
      <Cols
        extraSmallVerticalPaddingV2
        smallHorizontalPaddingV2
        onClick={onOpenDropdown}
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${publicaColors.lightGray}`,
          borderRadius: borderRadius.small,
          width: inputWidth || 'inherit',
          pointerEvents: isDisabled ? 'none' : 'all',
          cursor: 'pointer',
          transition: 'all 0.5s ease-in-out',
          background: isDisabled ? publicaColors.veryLightGray : 'inherit',
          ...style,
        }}
      >
        <Col style={{ width: `calc(100% - 16px - ${spacingV2.medium})` }}>
          <Text
            sizeV2
            small
            muted={isDisabled}
            style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            tKey={selectedOption ? selectedOption?.tKey : placeholderTKey}
          >
            {selectedOption ? selectedOption?.label : placeholder}
          </Text>
        </Col>
        <Col
          displayFlex
          style={{
            width: '16px',
            marginLeft: spacingV2.medium,
            alignItems: 'center',
          }}
        >
          <IconV2 name='caret' color={isDisabled ? publicaColors.lightGray : publicaColors.gray} />
        </Col>
      </Cols>

      <Popover
        id={Boolean(anchorEl) ? 'simple-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onCloseDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            padding: 0,
            overflow: 'hidden',
            width: popoverWidth,
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          {isSearchEnabled && (
            <Row smallPaddingV2 style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
              <TextInput
                enableClear
                fullWidth
                handleOnChange={(value: string) => setSearch(value.toLowerCase())}
                inputSize='small'
                isAutoFocused
                msDelay={0}
                placeholder={t('Search')}
                prefix='search'
              />
            </Row>
          )}
          <Row noPadding>
            <FixedSizeList
              height={displayedOptions.length >= 12 ? 360 : displayedOptions.length * itemSize}
              itemCount={displayedOptions.length}
              itemSize={itemSize}
              width='100%'
            >
              {({ index, style }) => (
                <div
                  style={{
                    paddingTop: index === 0 ? 0 : 'inherit',
                    paddingBottom: index === displayedOptions.length - 1 ? 0 : 'inherit',
                  }}
                >
                  <Option
                    className={`virtual-row-${index}`}
                    option={displayedOptions[index]}
                    onClick={(opt) => handleChange(opt)}
                    style={style}
                  />
                </div>
              )}
            </FixedSizeList>
          </Row>
        </Rows>
      </Popover>
    </>
  );
};
